import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gameService } from '../../../../services/gameService';
import { playerCardService } from '../../../../services/playerCardService';
import { sportStatsService } from '../../../../services/sportStatsService';
import { matchPointsService } from '../../../../services/tennis/matchPointsService';
import { projectTennisMatchStats } from '../../../../services/tennis/statsProjection';
import {
  appendPoint,
  createClientId,
  isStorageAvailable,
  loadQueue,
  popLastPoint,
  replaceQueue,
  unsyncedCount,
  updateLastPoint,
  type QueuedPoint,
} from '../../../../services/tennis/offlineQueue';
import { reduce, type MatchState, type Side } from '../../../../services/tennis/scoring';
import { getTennisMatchFormat } from '../../../../data/tennisMatchFormats';
import { getNetworkStatus, onNetworkChange } from '../../../../utils/mobile';
import { supabase } from '../../../../lib/supabase';
import type {
  Game,
  MatchParticipant,
  MatchPoint,
  PointEndReason,
  ServeOutcome,
} from '../../../../types';

export type ServeState = 'first' | 'second';

interface RosterPlayer {
  id: string;
  firstName: string;
  lastName: string;
}

/**
 * Loads a Tennis match, maintains the offline point queue, projects it
 * through the pure scoring engine, and exposes the actions the scoring pad
 * needs. All the state wiring for sc-338 lives here so TennisScoringScreen
 * itself stays presentational.
 */
export function useTennisScoringMatch(gameId: string | undefined) {
  const [loading, setLoading] = useState(true);
  const [game, setGame] = useState<Game | null>(null);
  const [participants, setParticipants] = useState<MatchParticipant[]>([]);
  const [ourPlayers, setOurPlayers] = useState<RosterPlayer[]>([]);
  const [points, setPoints] = useState<QueuedPoint[]>([]);
  const [online, setOnline] = useState(true);
  const [serveState, setServeState] = useState<ServeState>('first');
  const [completing, setCompleting] = useState(false);
  const [netPlayerId, setNetPlayerId] = useState<string | null>(null);
  const completedRef = useRef(false);
  const lastProjectedSetCountRef = useRef(0);
  const playerCardIdsRef = useRef<Map<string, string>>(new Map());
  // Serializes projectAndSaveStats calls (see its own comment) — never resolves to a
  // rejection, so plain .then() chaining is safe without a rejection handler at each link.
  const projectQueueRef = useRef<Promise<void>>(Promise.resolve());

  const format = useMemo(() => getTennisMatchFormat(game?.match_format?.id as string | undefined), [game?.match_format]);
  const isDoubles = game?.match_type === 'Doubles' || game?.match_type === 'Mixed Doubles';

  // ---- Initial load: game, participants, roster names, and a one-time merge of server + local-queue points. ----
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [gameData, participantRows, serverPoints] = await Promise.all([
          gameService.getGame(gameId),
          gameService.getMatchParticipants(gameId),
          matchPointsService.getPoints(gameId),
        ]);
        if (cancelled) return;

        setGame(gameData);
        setParticipants(participantRows);
        // Default: slot 1 serves our first service game (net = the other player, slot 2).
        setNetPlayerId(participantRows.find((p) => p.slot === 2)?.player_id ?? null);

        if (participantRows.length > 0) {
          const { data: playerRows } = await supabase
            .from('players')
            .select('id, first_name, last_name')
            .in('id', participantRows.map((p) => p.player_id));
          if (!cancelled && playerRows) {
            const bySlot = new Map(participantRows.map((p) => [p.player_id, p.slot]));
            setOurPlayers(
              [...playerRows]
                .sort((a, b) => (bySlot.get(a.id) ?? 0) - (bySlot.get(b.id) ?? 0))
                .map((p) => ({ id: p.id, firstName: p.first_name, lastName: p.last_name }))
            );
          }
        }

        const serverClientIds = new Set(serverPoints.map((p) => p.client_id));
        const localOnly = loadQueue(gameId).filter((p) => !serverClientIds.has(p.client_id));
        const merged: QueuedPoint[] = [
          ...serverPoints.map((p) => ({ ...p, synced: true })),
          ...localOnly,
        ].sort((a, b) => a.seq - b.seq);
        replaceQueue(gameId, merged);
        if (!cancelled) setPoints(merged);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  // ---- Connectivity + background sync. ----
  const flush = useCallback(async () => {
    if (!gameId) return;
    const result = await matchPointsService.flushQueue(gameId);
    setPoints(result);
  }, [gameId]);

  useEffect(() => {
    getNetworkStatus().then((status) => setOnline(status.connected));
    onNetworkChange((status: { connected: boolean }) => {
      setOnline(status.connected);
      if (status.connected) void flush();
    });
  }, [flush]);

  // Belt-and-suspenders periodic retry — some WebViews don't fire connectivity events reliably.
  useEffect(() => {
    const interval = setInterval(() => void flush(), 15000);
    return () => clearInterval(interval);
  }, [flush]);

  // ---- Engine projection. ----
  const enginePoints = useMemo(() => points.map((p) => ({ winner: p.won_by })), [points]);
  const state: MatchState = useMemo(() => reduce(format, enginePoints, { isDoubles }), [format, enginePoints, isDoubles]);

  const ourSetsWon = state.sets.filter((s) => s.ourGames > s.theirGames).length;
  const theirSetsWon = state.sets.length - ourSetsWon;

  /**
   * Project the point log into per-player sport_stats rows (sc-339 / Tennis
   * Phase 5) and upsert them. Always recomputes the FULL cumulative totals
   * from the current point log and overwrites — never increments — so it's
   * safe to call repeatedly (every set boundary, match completion, and after
   * an undo that rolls the log backward).
   *
   * The set-boundary and match-completion effects below both fire on the same
   * render for a match's final point (it completes the last set AND the
   * match at once), so two calls can be triggered back to back. upsertGameStats
   * is delete-then-insert, not an atomic upsert (sport_stats' unique index is
   * partial, which the JS client's upsert() can't target — see that method's
   * comment), so running two calls concurrently races: the second INSERT can
   * collide with rows the first one just wrote. Queued through
   * projectQueueRef so a second call always waits for the first to fully
   * settle before repeating the same (idempotent, if redundant) work.
   */
  const projectAndSaveStats = useCallback(() => {
    const run = async () => {
      if (!gameId || !game || participants.length === 0) return;
      try {
        const cardsByPlayerId = await Promise.all(
          participants.map(async (p) => {
            let cardId = playerCardIdsRef.current.get(p.player_id);
            if (!cardId) {
              const card = await playerCardService.getOrCreatePlayerCard(p.player_id, game.team_id);
              cardId = card.id;
              playerCardIdsRef.current.set(p.player_id, cardId);
            }
            return { playerId: p.player_id, cardId };
          })
        );

        const projection = projectTennisMatchStats({
          format,
          points,
          isDoubles,
          participantPlayerIds: participants.map((p) => p.player_id),
        });

        await Promise.all(
          cardsByPlayerId.map(({ playerId, cardId }) =>
            sportStatsService.upsertGameStats(cardId, game.team_id, gameId, game.date, projection[playerId] ?? {}, game.season ?? undefined)
          )
        );
      } catch (e) {
        console.error('[useTennisScoringMatch] Error projecting/saving match stats:', e);
      }
    };
    const next = projectQueueRef.current.then(run);
    projectQueueRef.current = next;
    return next;
  }, [gameId, game, participants, format, points, isDoubles]);

  // ---- Per-set projection: fires on every set boundary, in either direction (undo can roll it back). ----
  useEffect(() => {
    if (state.sets.length === lastProjectedSetCountRef.current) return;
    lastProjectedSetCountRef.current = state.sets.length;
    void projectAndSaveStats();
  }, [state.sets.length, projectAndSaveStats]);

  // ---- Completion: write back once, the instant the engine reports a winner. ----
  useEffect(() => {
    if (!gameId || !state.winner || completedRef.current) return;
    completedRef.current = true;
    setCompleting(true);
    void Promise.all([gameService.completeMatch(gameId, ourSetsWon, theirSetsWon), projectAndSaveStats()]).finally(() =>
      setCompleting(false)
    );
  }, [gameId, state.winner, ourSetsWon, theirSetsWon, projectAndSaveStats]);

  /**
   * Doubles: the server is simply "whichever of our two players isn't at
   * net," derived from the manual net toggle rather than the engine's fixed
   * A/B rotation slot — the engine doesn't know or care which named
   * individual served (score only depends on which SIDE served), so this is
   * purely a display/attribution layer the scorer fully controls. Trade-off:
   * unlike real doubles rules, this doesn't auto-alternate between partners
   * across our own service games — the scorer must tap the switch.
   */
  const serverPlayerIdForCurrentPoint = useCallback((): string | null => {
    if (state.server !== 'us') return null;
    if (!isDoubles) return participants[0]?.player_id ?? null;
    return participants.find((p) => p.player_id !== netPlayerId)?.player_id ?? null;
  }, [state.server, isDoubles, participants, netPlayerId]);

  /** Doubles only: swap which of our two players is at net — and, when we're serving, which one is credited as serving (they're complementary). */
  const toggleNetPlayer = useCallback(() => {
    if (participants.length < 2) return;
    setNetPlayerId((current) => participants.find((p) => p.player_id !== current)?.player_id ?? current);
  }, [participants]);

  const recordPoint = useCallback(
    (wonBy: Side, serveOutcome: ServeOutcome, endReason: PointEndReason | null = null) => {
      if (!gameId || !game || state.winner) return;
      const maxSeq = points.reduce((m, p) => Math.max(m, p.seq), 0);
      const point: MatchPoint = {
        game_id: gameId,
        seq: maxSeq + 1,
        client_id: createClientId(),
        won_by: wonBy,
        server: state.server,
        server_player_id: serverPlayerIdForCurrentPoint(),
        net_player_id: isDoubles ? netPlayerId : null,
        serve_outcome: serveOutcome,
        end_reason: endReason,
        set_index: state.sets.length,
        game_index: (state.currentSet?.ourGames ?? 0) + (state.currentSet?.theirGames ?? 0),
        is_tiebreak: state.game?.isTB ?? false,
      };
      setPoints(appendPoint(gameId, point));
      setServeState('first');
      void flush();
    },
    [gameId, game, state, points, serverPlayerIdForCurrentPoint, isDoubles, netPlayerId, flush]
  );

  /** "1st serve missed" toggles to 2nd serve; a second fault is a double fault and auto-awards the point. */
  const recordFault = useCallback(() => {
    if (serveState === 'first') {
      setServeState('second');
      return;
    }
    recordPoint(state.server === 'us' ? 'them' : 'us', 'double_fault', 'double_fault');
  }, [serveState, recordPoint, state.server]);

  const tapWon = useCallback(
    (side: Side) => {
      recordPoint(side, serveState === 'first' ? '1st_in' : '2nd_in');
    },
    [recordPoint, serveState]
  );

  /** Post-point detail sheet: reason tag, doubles winning-player credit, and optional Pro-tier tags, saved together in one update. */
  const tagLastPoint = useCallback(
    (
      updates: Pick<
        MatchPoint,
        'end_reason' | 'winning_player_id' | 'shot_type' | 'error_spot' | 'net_approach' | 'rally_bucket'
      >
    ) => {
      if (!gameId) return;
      setPoints(updateLastPoint(gameId, updates));
      void flush();
    },
    [gameId, flush]
  );

  const undo = useCallback(() => {
    if (!gameId || points.length === 0) return;
    const wasComplete = state.winner !== null;
    const { points: next, removed } = popLastPoint(gameId);
    setPoints(next);
    setServeState('first');
    completedRef.current = false;
    if (removed?.synced) {
      void matchPointsService.deletePoint(gameId, removed.seq).catch((e) => {
        console.error('[useTennisScoringMatch] Error deleting undone point on server:', e);
      });
    }
    if (wasComplete) {
      void gameService.reopenMatch(gameId).catch((e) => {
        console.error('[useTennisScoringMatch] Error reopening match after undo:', e);
      });
    }
  }, [gameId, points.length, state.winner]);

  return {
    loading,
    completing,
    game,
    format,
    isDoubles,
    ourPlayers,
    points,
    state,
    ourSetsWon,
    theirSetsWon,
    serveState,
    online,
    storageAvailable: isStorageAvailable(),
    unsyncedCount: unsyncedCount(points),
    netPlayerId,
    toggleNetPlayer,
    tapWon,
    recordFault,
    tagLastPoint,
    undo,
  };
}
